import { useAuth0 } from '@auth0/auth0-react'
import { Box } from '@mui/material'
import { useEffect, useRef } from 'react'
import TagManager from 'react-gtm-module'
import { Navigate, useLocation } from 'react-router-dom'
import ErrorDisplay from '~components/layout/error/error-display/error-display'
import LoadComponent from '~components/molecules/load-component/load-component'
import { Splash } from '~components/page-header/splash'
import { RedirectWithCountdown } from '~components/redirect-with-countdown'
import { useGetMe } from '~hooks/queries/me/use-get-me'
import { useConstructLinkWithPool } from '~hooks/use-construct-link-with-pool'

const LEGACY_DASHBOARD_PATH = /\/dashboard\//
const LEGACY_FORECAST_PATH = /\/panel\/financials\/forecast\//
const LEGACY_EARNINGS_PATH = /^\/financials\/earnings\/[^/]+\/pool-vessels\/?$/

export function PageNotFound() {
  const { pathname } = useLocation()
  const { isAuthenticated } = useAuth0()
  const { data, isLoading, isError } = useGetMe()
  const isIdPage = pathname.includes('404')
  const hasTracked = useRef(false)
  const dashboardLink = useConstructLinkWithPool('/dashboard/:pool')
  const forecastLink = useConstructLinkWithPool('/financials/forecast/:pool')
  const earningsLink = useConstructLinkWithPool('/financials/earnings/:pool')

  const legacyRedirect = [
    { pattern: LEGACY_DASHBOARD_PATH, link: dashboardLink },
    { pattern: LEGACY_FORECAST_PATH, link: forecastLink },
    { pattern: LEGACY_EARNINGS_PATH, link: earningsLink },
  ].find(({ pattern }) => pattern.test(pathname))?.link

  useEffect(() => {
    if (isLoading || hasTracked.current || legacyRedirect) return
    hasTracked.current = true

    TagManager.dataLayer({
      dataLayer: {
        event: 'internal_click',
        click_type: 'page_not_found',
        attempted_url: pathname,
        referrer: document.referrer || 'direct',
        is_authenticated: isAuthenticated,
        is_external_user: data && !data.isInternal,
        has_pools:
          (data?.activePools?.length ?? 0) > 0 ||
          (data?.profitCenterPools?.length ?? 0) > 0,
      },
    })
  }, [isLoading, pathname, isAuthenticated, data, legacyRedirect])

  if (isLoading) {
    return <LoadComponent />
  }

  if (!isIdPage && isAuthenticated && !isError && data && !data?.isInternal) {
    return <Navigate to="/404" />
  }

  if (legacyRedirect) {
    return (
      <>
        <Splash pageName="" omitPoolSelector />
        <Box sx={{ mt: 6 }}>
          <RedirectWithCountdown
            to={legacyRedirect}
            message="This page has moved. Please update your bookmarks."
          />
        </Box>
      </>
    )
  }

  return (
    <>
      <Splash pageName="" omitPoolSelector />
      <ErrorDisplay heading="Sorry, we couldn't find this page" isPageError />
    </>
  )
}
